<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router/auto'
import { sendVerificationCode } from '@/api/auth'
import { ApiError } from '@/api/client'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  // `signupOnly`: con el alta cerrada (el default) rebota a login — la cuenta
  // se pide por mail y se completa desde el link de la invitación.
  meta: { layout: 'blank', public: true, publicOnly: true, signupOnly: true },
})

const router = useRouter()

/**
 * Avanza de paso y **avisa si la navegación no ocurrió**.
 *
 * `router.push` no lanza: devuelve un `NavigationFailure` que, ignorado, deja
 * un botón que responde y no pasa nada — sin rastro en consola ni en pantalla.
 * Nos costó una sesión entera de diagnóstico a ciegas.
 */
const go = async (to: RouteLocationRaw) => {
  const failure = await router.push(to)

  if (failure)
    console.error('[register] navigation blocked:', failure)

  return failure
}

const email = ref('')
const loading = ref(false)
const error = ref('')

onMounted(() => {
  // El paso 1 (tipo de cuenta) es obligatorio: sin él, el alta no puede cerrar.
  if (!sessionStorage.getItem('bt_register_account_type'))
    router.replace({ name: 'register' })
})

const submit = async () => {
  error.value = ''
  if (!email.value.trim()) {
    error.value = 'Please enter your email address.'

    return
  }

  loading.value = true
  try {
    await sendVerificationCode(email.value.trim())
    sessionStorage.setItem('bt_register_email', email.value.trim())
    await go({ name: 'register-verify' })
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Something went wrong.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper"
    no-gutters
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Your email ✉️
          </h4>
          <p class="mb-0">
            We'll send a 4-digit verification code.
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="submit">
            <VRow>
              <VCol
                v-if="error"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                  density="compact"
                >
                  {{ error }}
                </VAlert>
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="email"
                  autofocus
                  label="Email"
                  type="email"
                  autocomplete="email"
                  placeholder="you@divecenter.com"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                  :loading="loading"
                >
                  Send verification code
                </VBtn>
              </VCol>

              <VCol
                cols="12"
                class="text-center text-base"
              >
                <span>Already have an account?</span>
                <RouterLink
                  class="text-primary d-inline-block ms-1"
                  :to="{ name: 'login' }"
                >
                  Log in
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
